import { PARKING_STATUS_TYPE } from "@mozioinc/query";
import { BUTTON_COLOR, BUTTON_VARIANT, Button, Container } from "@mozioinc/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ChevronSvg } from "./assets/chevron.svg";

import { CancelationDialog } from "./cancelation-dialog";
import { PurchaseSummary } from "./purchase-summary";
import type { ParkingReservation } from "@mozioinc/query";

type ReservationProps = {
  reservation?: ParkingReservation;
  onCancel: (reason: string) => void;
};

const Reservation = ({ reservation, onCancel }: ReservationProps) => {
  const { t } = useTranslation("parking:manage-reservation");
  const [openCancelationDialog, setOpenCancelationDialog] = useState(false);
  if (!reservation) {
    return;
  }
  const IS_CANCELLED = reservation.status === PARKING_STATUS_TYPE.CANCELLED;
  const IS_COMPLETED = reservation.status === PARKING_STATUS_TYPE.COMPLETED;
  const arrivalDateTime = new Date(reservation.arrivalDateTime);
  const IS_CANCELLABLE =
    arrivalDateTime.setTime(
      arrivalDateTime.getTime() -
        reservation.cancellation.noticeInHours * 3_600_000,
    ) > Date.now();

  return (
    <div className="flex flex-1 items-center bg-white sm:bg-neutral-100">
      <Container className="mt-2 w-full max-w-2xl flex-1 rounded-lg bg-white p-5">
        <h1 className="text-2xl font-bold">
          {t("Reservation number: {{id}}", { id: reservation.id })}
        </h1>

        {!IS_CANCELLED && IS_CANCELLABLE && (
          <div className="mt-2 rounded-md bg-neutral-300 p-2 text-white">
            <p className="text-xs font-normal text-gray-500">
              {t(
                "Modifying a reservation online is not supported at the moment. You can cancel this reservation for a full refund and make another booking or contact our support team.",
              )}
            </p>
          </div>
        )}

        {IS_CANCELLED && (
          <div className="mt-2 rounded-md bg-red-700 p-2 text-white">
            <p className="text-xs font-normal text-white">
              {t("Reservation canceled")}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 p-3">
          <PurchaseSummary
            location={reservation.location}
            name={reservation.parkingName}
            showPrice={false}
            arrivalDateTime={new Date(reservation.arrivalDateTime)}
            departureDateTime={new Date(reservation.departureDateTime)}
            paymentDetails={{
              totalPrice: reservation.payment.totalPrice,
              finalPrice: reservation.payment.finalPrice,
              coupon: reservation.payment.coupon,
              currency: reservation.payment.currency,
            }}
          />
        </div>

        {IS_COMPLETED && IS_CANCELLABLE && (
          <>
            <Button
              variant={BUTTON_VARIANT.SOLID}
              color={BUTTON_COLOR.PRIMARY}
              className="w-full"
              onClick={() => setOpenCancelationDialog(true)}
            >
              {t("Cancel reservation")}
              <ChevronSvg className="text-xl" />
            </Button>
            <CancelationDialog
              open={openCancelationDialog}
              onCancel={(reason) => {
                setOpenCancelationDialog(false);
                onCancel(reason);
              }}
              onClose={() => setOpenCancelationDialog(false)}
              amount={reservation.payment.finalPrice}
            />

            <div className="mt-2 rounded-md bg-green-500 p-2 text-white">
              <p className="text-sm">
                {t("Your reservation is fully refundable.")}
              </p>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export { Reservation };
